import type { Patch } from "../engine/types";
import { renderPatchToWav } from "../engine/wavExport";
import { defaultPatch, clonePatch, coercePatch } from "../engine/defaultPatch";

export interface ToolbarOptions {
  getPatch: () => Patch;
  onLoad: (patch: Patch) => void;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function safeName(patch: Patch): string {
  return (patch.name || "patch").replace(/[^a-z0-9_-]+/gi, "_");
}

export function buildToolbar(container: HTMLElement, opts: ToolbarOptions): void {
  container.innerHTML = "";

  const saveBtn = document.createElement("button");
  saveBtn.textContent = "Save patch";
  saveBtn.addEventListener("click", () => {
    const patch = opts.getPatch();
    const blob = new Blob([JSON.stringify(patch, null, 2)], { type: "application/json" });
    downloadBlob(blob, `${safeName(patch)}.patch.json`);
  });

  const loadLabel = document.createElement("label");
  loadLabel.className = "file";
  loadLabel.textContent = "Load patch";
  const fileInput = document.createElement("input");
  fileInput.type = "file";
  fileInput.accept = "application/json,.json";
  fileInput.addEventListener("change", async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    try {
      const patch = coercePatch(JSON.parse(await file.text()));
      opts.onLoad(patch);
    } catch {
      alert("Could not read that patch file.");
    }
    fileInput.value = "";
  });
  loadLabel.append(fileInput);

  const wavBtn = document.createElement("button");
  wavBtn.textContent = "Export WAV";
  wavBtn.addEventListener("click", async () => {
    const patch = opts.getPatch();
    const original = wavBtn.textContent;
    wavBtn.disabled = true;
    wavBtn.textContent = "Rendering…";
    try {
      const blob = await renderPatchToWav(patch);
      downloadBlob(blob, `${safeName(patch)}.wav`);
    } finally {
      wavBtn.disabled = false;
      wavBtn.textContent = original;
    }
  });

  const resetBtn = document.createElement("button");
  resetBtn.textContent = "Reset";
  resetBtn.addEventListener("click", () => opts.onLoad(clonePatch(defaultPatch)));

  container.append(saveBtn, loadLabel, wavBtn, resetBtn);
}
